import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Eye, Heart, Smile, Ban, Contact as ContactIcon, Handshake, Send, Bell, Pencil, LogOut, EyeOff, Trash2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PushToggle from "@/components/umaang/PushToggle";
import ShareYourStorySection from "@/components/umaang/ShareYourStorySection";

function Stat({ icon: I, label, value, testId, to, color = "#C85A53" }) {
  const inner = (
    <div className="p-6 border card-hover relative overflow-hidden" data-testid={testId} style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-15" style={{ background: color }} />
      <div className="relative">
        <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${color}18`, color }}>
          <I className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="font-display text-4xl mt-4">{value ?? "—"}</div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--umg-text-2)] mt-1">{label}</div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function ListCard({ items, title, emptyMsg, render }) {
  return (
    <div className="p-6 border" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
      <h3 className="font-display text-2xl">{title}</h3>
      {items.length === 0 ? (
        <div className="mt-4 text-sm text-[color:var(--umg-text-2)]">{emptyMsg}</div>
      ) : (
        <ul className="mt-4 space-y-2 max-h-72 overflow-y-auto">
          {items.map((it, i) => (
            <li key={`${it.at || it.id || "item"}-${i}`} className="text-sm border-b py-2 flex items-center justify-between gap-2" style={{ borderColor: "var(--umg-border)" }}>
              {render(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Insights() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [stats, setStats] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(null); // 'hide' | 'delete'
  // OTP-based delete flow state (must be before any early return)
  const [otpStep, setOtpStep] = useState(0); // 0=start, 1=OTP sent
  const [otpCode, setOtpCode] = useState("");
  const [otpReason, setOtpReason] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpMasked, setOtpMasked] = useState("");

  const load = () => {
    api.get("/insights").then(({ data }) => setD(data)).catch((e) => toast.error(formatError(e)));
    api.get("/me/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!d || !stats) return <div className="max-w-6xl mx-auto p-10 text-[color:var(--umg-text-2)]">Loading…</div>;

  const doLogout = () => { logout(); nav("/"); };
  const doHide = async () => {
    try { await api.post("/account/deactivate"); toast.success("Profile hidden. Others won't see you."); setConfirmOpen(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doReactivate = async () => {
    try { await api.post("/account/reactivate"); toast.success("Profile is visible again."); load(); window.location.reload(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doDelete = async () => {
    try {
      await api.delete("/account");
      toast.success("Account deleted.");
      logout();
      nav("/");
    } catch (e) { toast.error(formatError(e)); }
  };

  // OTP-based delete flow (new)
  const requestDeleteOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await api.post("/profile/delete/request-otp");
      setOtpMasked(data.mobile_masked || "your mobile");
      setOtpStep(1);
      toast.success("OTP sent — check your SMS");
    } catch (e) { toast.error(formatError(e)); }
    finally { setOtpBusy(false); }
  };
  const confirmDeleteOtp = async () => {
    if (!otpCode.trim()) return toast.error("Enter the OTP");
    setOtpBusy(true);
    try {
      await api.post("/profile/delete/confirm", { code: otpCode.trim(), reason: otpReason });
      toast.success("Account deleted. Goodbye 🙏");
      setTimeout(() => { logout(); nav("/"); }, 1200);
    } catch (e) { toast.error(formatError(e)); }
    finally { setOtpBusy(false); }
  };
  const resetOtp = () => { setOtpStep(0); setOtpCode(""); setOtpReason(""); setOtpBusy(false); };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-[#C85A53] font-semibold">{t("only_you")}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Who saw you today.</h1>
        </div>
        <PushToggle />
      </div>

      {/* Stat grid */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat icon={Eye} label="Profile views" value={stats.profile_views} testId="stat-views" color="#2563EB" />
        <Stat icon={Heart} label="Likes received" value={stats.likes_received} testId="stat-likes" color="#C85A53" />
        <Stat icon={Smile} label="Emojis received" value={stats.emojis_received} testId="stat-emojis" color="#F59E0B" />
        <Stat icon={Bell} label="Connect requests" value={stats.connect_pending} testId="stat-requests" to="/inbox?tab=received" color="#7C3AED" />
        <Stat icon={Handshake} label="Accepted connections" value={stats.connect_accepted} testId="stat-accepted" to="/inbox?tab=accepted" color="#059669" />
        <Stat icon={Send} label="Requests sent" value={stats.connect_sent} testId="stat-sent" to="/inbox?tab=sent" color="#0EA5E9" />
        <Stat icon={ContactIcon} label="Contact viewed by" value={stats.contact_views_count} testId="stat-contact" to="/inbox?tab=contact" color="#DB2777" />
        <Stat icon={Ban} label="Blocked by others" value={stats.blocked_me_count} testId="stat-blocked-me" color="#6B7280" />
      </div>

      {/* Recent lists */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <ListCard
          items={d.views}
          title="Recent viewers"
          emptyMsg="No one has viewed you yet."
          render={(v) => (<><span>{v.viewer_name || "Someone"}</span><span className="text-[10px] text-[color:var(--umg-text-2)]">{new Date(v.at).toLocaleString()}</span></>)}
        />
        <ListCard
          items={d.likes}
          title="Likes received"
          emptyMsg="No likes yet."
          render={(l) => (<><span>{l.from_name || "Someone"}</span><span className="text-[10px] text-[color:var(--umg-text-2)]">{new Date(l.at).toLocaleString()}</span></>)}
        />
        <ListCard
          items={d.emojis}
          title="Emojis received"
          emptyMsg="No emojis yet."
          render={(e) => (<><span className="flex items-center gap-2"><span className="text-lg">{e.emoji}</span>{e.from_name || "Someone"}</span><span className="text-[10px] text-[color:var(--umg-text-2)]">{new Date(e.at).toLocaleString()}</span></>)}
        />
      </div>

      {/* Share your success story */}
      <ShareYourStorySection />

      {/* Account management */}
      <div className="mt-10 border p-6" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--umg-text-2)]">
          <Settings className="w-3.5 h-3.5" /> Account
        </div>
        <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/profile/edit" data-testid="acc-edit" className="p-4 border rounded-lg card-hover" style={{ borderColor: "var(--umg-border)" }}>
            <Pencil className="w-4 h-4 text-[#C85A53]" />
            <div className="mt-2 font-medium text-sm">Edit profile</div>
            <div className="text-xs text-[color:var(--umg-text-2)] mt-1">Update details, photos and preferences.</div>
          </Link>
          {user?.hidden ? (
            <button data-testid="acc-reactivate" onClick={doReactivate} className="text-left p-4 border rounded-lg card-hover" style={{ borderColor: "var(--umg-border)" }}>
              <Eye className="w-4 h-4 text-[#4F7942]" />
              <div className="mt-2 font-medium text-sm">Reactivate profile</div>
              <div className="text-xs text-[color:var(--umg-text-2)] mt-1">Currently hidden — make visible again.</div>
            </button>
          ) : (
            <button data-testid="acc-hide" onClick={() => setConfirmOpen("hide")} className="text-left p-4 border rounded-lg card-hover" style={{ borderColor: "var(--umg-border)" }}>
              <EyeOff className="w-4 h-4 text-[#C85A53]" />
              <div className="mt-2 font-medium text-sm">Hide profile</div>
              <div className="text-xs text-[color:var(--umg-text-2)] mt-1">Pause matches without deleting.</div>
            </button>
          )}
          <button data-testid="acc-logout" onClick={doLogout} className="text-left p-4 border rounded-lg card-hover" style={{ borderColor: "var(--umg-border)" }}>
            <LogOut className="w-4 h-4" />
            <div className="mt-2 font-medium text-sm">Sign out</div>
            <div className="text-xs text-[color:var(--umg-text-2)] mt-1">End session on this device.</div>
          </button>
          <button data-testid="acc-delete" onClick={() => setConfirmOpen("delete")} className="text-left p-4 border rounded-lg card-hover" style={{ borderColor: "#C85A53" }}>
            <Trash2 className="w-4 h-4 text-[#C85A53]" />
            <div className="mt-2 font-medium text-sm text-[#C85A53]">Delete account</div>
            <div className="text-xs text-[color:var(--umg-text-2)] mt-1">Permanent. Cannot be undone.</div>
          </button>
        </div>
      </div>

      {/* Confirm dialogs */}
      <Dialog open={confirmOpen === "hide"} onOpenChange={(v) => !v && setConfirmOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hide profile?</DialogTitle>
            <DialogDescription>Your profile will not appear in matches. All data is preserved — reactivate anytime.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="confirm-hide" onClick={doHide} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">Hide profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen === "delete"} onOpenChange={(v) => { if (!v) { setConfirmOpen(null); resetOtp(); } }}>
        <DialogContent data-testid="delete-otp-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#B91C1C]">Danger zone — Delete account</DialogTitle>
            <DialogDescription>
              This is <b>permanent</b>. Photos, messages, likes, connections, and family chats will be removed. Payment records are retained per Indian tax law. We'll SMS an OTP to your registered mobile to confirm this action.
            </DialogDescription>
          </DialogHeader>
          {otpStep === 0 ? (
            <div className="space-y-3">
              <label className="text-xs text-[#7F1D1D]">Why are you leaving? (optional)</label>
              <textarea
                data-testid="delete-reason"
                value={otpReason}
                onChange={(e) => setOtpReason(e.target.value)}
                placeholder="Found my partner elsewhere / privacy / not useful …"
                rows={2}
                className="w-full border rounded-lg p-2 text-sm"
                style={{ borderColor: "#FCA5A5" }}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => { setConfirmOpen(null); resetOtp(); }}>Cancel</Button>
                <Button data-testid="delete-send-otp" onClick={requestDeleteOtp} disabled={otpBusy} className="btn-pill bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                  {otpBusy ? "Sending…" : "Send OTP to my mobile"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#7F1D1D]">OTP sent to <b>{otpMasked}</b>. Enter it to permanently delete your account.</p>
              <Input
                data-testid="delete-otp-input"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="Enter 6-digit OTP"
                inputMode="numeric"
                className="max-w-xs font-mono tracking-widest"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button data-testid="delete-confirm-otp" onClick={confirmDeleteOtp} disabled={otpBusy || otpCode.length < 4} className="btn-pill bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                  {otpBusy ? "Deleting…" : "Confirm — delete forever"}
                </Button>
                <button type="button" onClick={requestDeleteOtp} disabled={otpBusy} className="text-xs text-[#7F1D1D] underline">Resend OTP</button>
                <Button variant="ghost" onClick={() => { setConfirmOpen(null); resetOtp(); }}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
